import fs from 'node:fs'
import path from 'node:path'
import type { DevScriptPortResult } from '@cli-types/next-adapter/init.js'
import type { ProjectPackageJson } from '@cli-types/next-adapter/utils.js'
import { DEFAULT_DEV_PORT } from '@cli-constants/scaffolding.js'
import { normalizeWhitespace } from '@cli-utils/text.js'
import { detectIndent } from '@cli-utils/text.js'
import { detectDevPortFromScript } from './detect-dev-port-from-script.js'
import { isPlainNextDevScript } from './is-plain-next-dev-script.js'

export function ensureDefaultDevScriptPort(cwd: string): DevScriptPortResult {
  const pkgPath = path.join(cwd, 'package.json')
  if (!fs.existsSync(pkgPath)) {
    return { status: 'missing-package', port: DEFAULT_DEV_PORT }
  }

  const raw = fs.readFileSync(pkgPath, 'utf-8')
  let pkg: ProjectPackageJson

  try {
    pkg = JSON.parse(raw) as ProjectPackageJson
  } catch {
    return { status: 'missing-package', port: DEFAULT_DEV_PORT }
  }

  const devScript = pkg.scripts?.dev
  if (typeof devScript !== 'string' || devScript.trim().length === 0) {
    return { status: 'missing-script', port: DEFAULT_DEV_PORT }
  }

  const explicitPort = detectDevPortFromScript(devScript)
  if (explicitPort) {
    return { status: 'preserved', port: explicitPort }
  }

  if (!isPlainNextDevScript(devScript)) {
    return { status: 'skipped', port: DEFAULT_DEV_PORT }
  }

  const normalizedScript = `${normalizeWhitespace(devScript)} --port ${DEFAULT_DEV_PORT}`
  pkg.scripts = {
    ...pkg.scripts,
    dev: normalizedScript
  }

  const newline = raw.includes('\r\n') ? '\r\n' : '\n'
  const indent = detectIndent(raw)
  fs.writeFileSync(pkgPath, `${JSON.stringify(pkg, null, indent)}${newline}`, 'utf-8')

  return { status: 'updated', port: DEFAULT_DEV_PORT }
}
